import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { notify } from '@/lib/notifications/engine';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AgreementLineSchedulePanel } from '@/pages/participants/agreements/AgreementLineSchedulePanel';
import { ParticipantScheduleRows } from './ParticipantScheduleRows';
import { ParticipantRostersTab } from './ParticipantRostersTab';
import { runGenerate } from '@/lib/scheduleHome';

const EMPTY_GEN = { mode: 'PRESET', preset: 'NEXT_4_WEEKS', from_date: '', to_date: '', count: '', support_ratio: '1:1' };

/**
 * RP-3 Option B — Participant ▸ Schedule ▸ Schedules: the primary scheduling home.
 * Composition only: ACTIVE agreement lines (existing AgreementLineSchedulePanel → E2), the ONE schedule table (→ E1),
 * and the read-only generated-shift list. No second scheduler; every write goes through E1/E2.
 */
export function ParticipantSchedulesTab({ participantId, perms = [], fundingPlans = [], branches = [], focusAgreementId = null, onFocusConsumed }) {
  const navigate = useNavigate();
  const [agreements, setAgreements] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genForm, setGenForm] = useState(EMPTY_GEN);
  const [genOpenFor, setGenOpenFor] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const canGenerate = perms.includes('roster.manage');

  const load = useCallback(async () => {
    setLoading(true);
    const [agRes, siRes] = await Promise.allSettled([
      api.get(`/agreements/by-participant/${participantId}`),
      api.get(`/services/participant-items?participant_id=${participantId}`),
    ]);
    if (agRes.status === 'fulfilled') setAgreements(agRes.value.data?.data || []);
    if (siRes.status === 'fulfilled') setItems((siRes.value.data?.data || []).filter((i) => i.participant_id === participantId));
    setLoading(false);
  }, [participantId]);

  useEffect(() => { load(); }, [load]);

  const onGenerate = async (id, body) => {
    setGenerating(id);
    await runGenerate({ api, toast, navigate, notify }, id, body, () => { setGenOpenFor(null); load(); setRefreshKey((k) => k + 1); });
    setGenerating(null);
  };

  const active = agreements.filter((a) => (a.effective_status || a.status) === 'ACTIVE');
  const others = agreements.filter((a) => (a.effective_status || a.status) !== 'ACTIVE');

  return (
    <div className="space-y-4" data-testid="participant-schedules-home">
      <div>
        <h2 className="text-lg font-bold">Schedules</h2>
        <p className="text-xs text-muted-foreground">One scheduler: schedules are created from a line of an ACTIVE service agreement and generate shifts onto the Scheduling Board (Final PRD §8–§10).</p>
      </div>
      <Card>
        <CardHeader className="py-3"><CardTitle className="text-sm">Agreement lines</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {loading ? <p className="text-xs text-muted-foreground">Loading…</p>
          : active.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="schedules-no-active-agreement">No ACTIVE service agreement — activate one first (§8). Schedules can only be created from an ACTIVE agreement line.</p>
          ) : active.map((a) => (
            <div key={a.id} data-testid={`schedules-agreement-${a.id}`}>
              <p className="text-xs font-medium mb-1">{a.agreement_no || 'Agreement'} · v{a.version_no} · {a.start_date} → {a.authority_end_date || a.end_date}</p>
              <AgreementLineSchedulePanel agreement={a} perms={perms} fundingPlans={fundingPlans} branches={branches}
                focus={focusAgreementId === a.id ? 'schedule' : null} onFocusConsumed={onFocusConsumed} onScheduled={() => { load(); setRefreshKey((k) => k + 1); }} />
            </div>
          ))}
          {others.length > 0 && <p className="text-[10px] text-muted-foreground" data-testid="schedules-inactive-note">{others.length} other agreement version(s) are not ACTIVE and cannot be scheduled.</p>}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="py-3"><CardTitle className="text-sm">Participant schedules</CardTitle></CardHeader>
        <CardContent className="p-0">
          <ParticipantScheduleRows items={items} generating={generating} genForm={genForm} setGenForm={setGenForm} genOpenFor={genOpenFor} setGenOpenFor={setGenOpenFor} onGenerate={onGenerate} canGenerate={canGenerate} showRate={false} />
        </CardContent>
      </Card>
      <div data-testid="schedules-rosters-section">
        <ParticipantRostersTab participantId={participantId} refreshKey={refreshKey} />
      </div>
    </div>
  );
}

export default ParticipantSchedulesTab;
